import { reportHandledError } from './bugsnag';

// Vite's preload helper adds a lazily imported chunk's CSS as <link rel=stylesheet crossorigin> and
// rejects the import if that link fails. A copy of the file cached from a plain <link> without an
// Access-Control-Allow-Origin header fails it (the blank /hike-planner behind #2012), even though the
// same copy would load fine without crossorigin.
//
// So on a CSS preload failure, cancel the rejection (Vite then goes on to import the chunk), load the
// stylesheet again without crossorigin, and report it: the app still mounts, styled, and Bugsnag
// shows whether this is still happening.
const CSS_PRELOAD_FAILURE = /^Unable to preload CSS for (.+)$/;

type Reporter = (error: unknown, context: Record<string, unknown>) => void;

const installed = new WeakSet<Window>();

export function installCssPreloadFallback(win: Window = window, report: Reporter = reportHandledError): void {
  if (installed.has(win)) return;
  installed.add(win);

  win.addEventListener('vite:preloadError', (event) => {
    const error = (event as Event & { payload?: unknown }).payload;
    const href = error instanceof Error ? CSS_PRELOAD_FAILURE.exec(error.message)?.[1] : undefined;
    if (!href) return;

    event.preventDefault();

    const link = win.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    win.document.head.appendChild(link);

    report(error, { href, recovery: 'reloaded stylesheet without crossorigin' });
  });
}
